/**
 * InputRut — input único de RUT chileno para todo el portal.
 *
 * Encapsula la doctrina RUT en un solo lugar para que NINGÚN formulario
 * pueda olvidarse de validar (H17):
 *  - Formatea en vivo mientras se escribe (`XX.XXX.XXX-K`).
 *  - Valida módulo 11 y reporta validez + RUT canónico (sin puntos) al padre.
 *  - Muestra error inline cuando el valor es inválido (tras tocar el campo).
 *
 * Es controlado: `value` es el string formateado que muestra el input; el
 * padre lo guarda tal cual. En cada cambio recibe ademas `{ valid, canonico }`
 * para gate-ar el submit y enviar el canónico al backend.
 *
 * Storage canónico, display con puntos — convención #6/#13, doctrina RUT
 * H1-H4. El backend revalida con `RutChileno`/`normalizar_rut_chileno`.
 */
import { useState } from 'react';
import { formatearRutEnVivo, validarRutChileno } from '../../lib/format';

export interface RutValidez {
  valid: boolean;
  canonico: string | null;
}

interface InputRutProps {
  value: string;
  onChange: (formateado: string, validez: RutValidez) => void;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Si false, un valor vacío NO muestra error (RUT opcional). Default true. */
  requerido?: boolean;
  className?: string;
  'aria-label'?: string;
}

const CLASE_BASE =
  'w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 ' +
  'text-text-primary px-3 py-2 text-sm';

export function InputRut({
  value,
  onChange,
  id,
  placeholder = '12.345.678-9',
  disabled = false,
  requerido = true,
  className = '',
  'aria-label': ariaLabel,
}: InputRutProps) {
  const [tocado, setTocado] = useState(false);
  const vacio = value.trim() === '';
  const validez = validarRutChileno(value);
  const mostrarError =
    tocado && !validez.valid && (!vacio || requerido);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const formateado = formatearRutEnVivo(e.target.value);
    onChange(formateado, validarRutChileno(formateado));
  }

  return (
    <>
      <input
        type="text"
        // `text` y NO `numeric`: el dígito verificador del RUT puede ser "K",
        // y el teclado numérico de móvil no ofrece letras → el vecino no podía
        // ingresar RUTs terminados en K (bug reportado 2026-07-06).
        inputMode="text"
        id={id}
        value={value}
        onChange={handleChange}
        onBlur={() => setTocado(true)}
        maxLength={12}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={mostrarError || undefined}
        className={`${CLASE_BASE} ${className}`.trim()}
      />
      {mostrarError && (
        <p className="text-xs text-state-danger mt-1" role="alert">
          {vacio
            ? 'El RUT es obligatorio.'
            : 'RUT inválido — revisa el dígito verificador.'}
        </p>
      )}
    </>
  );
}
